import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { EmptyState, Page, PageHeader, TableSkeleton } from "../../../components/ui";
import { getPermissions, setPermissions } from "../../../services/permissions";
import "../ads/ad-form.css";
import "./permissions.css";

/** Label keys of the content categories. */
const CATEGORY_LABELS = {
  video: "Videos",
  tvshow: "Series",
  podcast: "Podcasts",
  channel: "Live channels",
};

/** Languages of the permissions, in their first order of appearance. */
export const permissionLanguages = (permissions) => [
  ...new Set(Object.values(permissions).flatMap((languages) => Object.keys(languages))),
];

/** Permissions sent back by the API, without its own fields. */
export const editablePermissions = (permission) => {
  // eslint-disable-next-line no-unused-vars
  const { createdAt, updatedAt, __v, tvchannel, _id, ...categories } = permission || {};
  return categories;
};

const PagesPermissions = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [permissionId, setPermissionId] = useState("");
  const [saved, setSaved] = useState({});
  const [draft, setDraft] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    getPermissions()
      .then((data) => {
        const categories = editablePermissions(data?.permission);
        setPermissionId(data?.permission?._id || "");
        setSaved(categories);
        setDraft(categories);
      })
      .catch((error) => console.error("Error fetching data:", error))
      .finally(() => setLoading(false));
  }, []);

  const languages = useMemo(() => permissionLanguages(draft), [draft]);
  const dirty = JSON.stringify(draft) !== JSON.stringify(saved);

  const setLanguage = (category, language, checked) =>
    setDraft((current) => ({
      ...current,
      [category]: { ...current[category], [language]: checked },
    }));
  const setCategory = (category, checked) =>
    setDraft((current) => ({
      ...current,
      [category]: Object.fromEntries(
        Object.keys(current[category]).map((language) => [language, checked])
      ),
    }));

  const handleSave = async () => {
    setSaving(true);
    try {
      await setPermissions(permissionId, draft);
      setSaved(draft);
      toast.success(t("Routes updated successfully"));
    } catch (error) {
      console.error("Error updating data:", error);
      toast.error(t("Error updating data"));
    } finally {
      setSaving(false);
    }
  };

  const categories = Object.entries(draft);
  return (
    <Page>
      <PageHeader
        title={t("Main Menu")}
        subtitle={t("Categories shown in the main menu of the apps, per language")}
        actions={
          <>
            {dirty && (
              <button
                type="button"
                className="ui-btn ui-btn-secondary"
                onClick={() => setDraft(saved)}
              >
                {t("cancel")}
              </button>
            )}
            <button
              type="button"
              className="ui-btn"
              disabled={!dirty || saving}
              onClick={handleSave}
            >
              {saving && <span className="ad-spinner" aria-hidden="true" />}
              {t("Save")}
            </button>
          </>
        }
      />

      <section className="ui-card">
        {loading ? (
          <TableSkeleton rows={4} />
        ) : categories.length === 0 ? (
          <EmptyState icon="ri-translate-2">{t("No Data Found")}</EmptyState>
        ) : (
          <div className="ui-table-wrap">
            <table className="ui-table permissions-table">
              <thead>
                <tr>
                  <th>{t("Category")}</th>
                  <th>{t("All")}</th>
                  {languages.map((language) => (
                    <th key={language}>{t(language)}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {categories.map(([category, values]) => {
                  const label = t(CATEGORY_LABELS[category] || category);
                  const states = Object.values(values);
                  const all = states.length > 0 && states.every(Boolean);
                  const some = states.some(Boolean);
                  return (
                    <tr key={category}>
                      <td>
                        <span className="ui-media-title">{label}</span>
                        <span className="ui-media-meta">
                          {t("languageCount", { count: states.filter(Boolean).length })}
                        </span>
                      </td>
                      <td>
                        <input
                          type="checkbox"
                          aria-label={`${label} : ${t("All")}`}
                          checked={all}
                          ref={(node) => node && (node.indeterminate = some && !all)}
                          onChange={(e) => setCategory(category, e.target.checked)}
                        />
                      </td>
                      {languages.map((language) => (
                        <td key={language}>
                          {language in values ? (
                            <input
                              type="checkbox"
                              aria-label={`${label} : ${t(language)}`}
                              checked={Boolean(values[language])}
                              onChange={(e) =>
                                setLanguage(category, language, e.target.checked)
                              }
                            />
                          ) : (
                            <span className="ui-cell-muted">—</span>
                          )}
                        </td>
                      ))}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </Page>
  );
};

export default PagesPermissions;
